import { RulesOperator } from '../enums/template';

/**
 * Safe evaluator for template show/hide rules such as
 *   ([P1.S1.Q1] EqualsTo [Yes]) AND ([P1.S1.Q2] IsEmpty)
 *
 * Answers and rule values come from people filling in or editing a form, so they are only ever
 * compared as data. Nothing in this file builds or runs code (no eval / new Function).
 *
 * Behaviour notes:
 *  - AND binds tighter than OR; brackets and the symbols && / || are also accepted.
 *  - Text between conditions that is not AND / OR / brackets makes the expression unreadable,
 *    and an unreadable expression SHOWS the item (same as having no rule).
 *  - An unknown operator makes that one condition false.
 */

export interface Rule {
  fullCondition: string;
  field: string;
  operator: string;
  value: string | null;
}

export function extractRules(rulesExpression: string): Rule[] {
  const conditionRegex = /(\(\s*\[(\w+(?:\.\w+)*)\]\s+(\w+)(?:\s+\[(.*?)\])?\s*\))/g;
  const results: Rule[] = [];
  let match;

  while ((match = conditionRegex.exec(rulesExpression)) !== null) {
    const [, fullCondition, field, operator, value] = match;
    results.push({ fullCondition, field, operator, value: value ?? null });
  }

  return results;
}

/** Turn a rule value into text. Editors sometimes wrap text values in quotes, e.g. ["abc"]. */
function valueText(value: string | null): string {
  const text = String(value);
  const quoted = text.match(/^(['"])(.*)\1$/s);
  return quoted ? quoted[2] : text;
}

function evaluateRule(rule: Rule, getFieldValue: (fieldId: string) => unknown): boolean {
  const answer = getFieldValue(rule.field) ?? '';
  const operator = RulesOperator[rule.operator as keyof typeof RulesOperator];
  const listValues = (rule.value ?? '').split(',');

  switch (operator) {
    case RulesOperator.EqualsTo:
      return Array.isArray(answer) ? answer.includes(rule.value) : String(answer) === String(rule.value);

    case RulesOperator.NotEqualsTo:
      return Array.isArray(answer) ? !answer.includes(rule.value) : String(answer) !== String(rule.value);

    case RulesOperator.LessThan:
      return parseFloat(String(answer)) < parseFloat(valueText(rule.value));

    case RulesOperator.GreaterThan:
      return parseFloat(String(answer)) > parseFloat(valueText(rule.value));

    case RulesOperator.LessThanEqualTo:
      return parseFloat(String(answer)) <= parseFloat(valueText(rule.value));

    case RulesOperator.GreaterThanEqualTo:
      return parseFloat(String(answer)) >= parseFloat(valueText(rule.value));

    case RulesOperator.BeginsWith:
      return String(answer).startsWith(valueText(rule.value));

    case RulesOperator.EndWith:
      return String(answer).endsWith(valueText(rule.value));

    case RulesOperator.Contains:
      return String(answer).includes(valueText(rule.value));

    case RulesOperator.DoesNotContains:
      return !String(answer).includes(valueText(rule.value));

    case RulesOperator.Includes:
      return Array.isArray(answer)
        ? listValues.some(item => answer.includes(item))
        : listValues.includes(answer as string);

    // Excludes means "none of the listed values is selected". Matches the phone app (the old web meaning was "at least one is not selected").
    case RulesOperator.Excludes:
      return Array.isArray(answer)
        ? !listValues.some(item => answer.includes(item))
        : !listValues.includes(answer as string);

    case RulesOperator.IsEmpty:
    case RulesOperator.IsFalse:
      return !answer;

    case RulesOperator.IsNotEmpty:
    case RulesOperator.IsTrue:
      return !!answer;

    default:
      return false;
  }
}

type Token = { kind: 'term'; result: boolean } | { kind: 'and' | 'or' | 'open' | 'close' };

/** Reads the text between terms. Only whitespace, AND, OR, &&, || and brackets are allowed. */
function pushJoiners(text: string, tokens: Token[]): boolean {
  const joiner = /\s+|\(|\)|AND\b|OR\b|&&|\|\|/iy;
  let at = 0;
  while (at < text.length) {
    joiner.lastIndex = at;
    const match = joiner.exec(text);
    if (!match) return false;
    const word = match[0].toUpperCase();
    if (word === '(') tokens.push({ kind: 'open' });
    else if (word === ')') tokens.push({ kind: 'close' });
    else if (word === 'AND' || word === '&&') tokens.push({ kind: 'and' });
    else if (word === 'OR' || word === '||') tokens.push({ kind: 'or' });
    at += match[0].length;
  }
  return true;
}

function tokenise(rulesExpression: string, getFieldValue: (fieldId: string) => unknown): Token[] | null {
  const tokens: Token[] = [];
  let at = 0;
  for (const rule of extractRules(rulesExpression)) {
    const start = rulesExpression.indexOf(rule.fullCondition, at);
    if (!pushJoiners(rulesExpression.slice(at, start), tokens)) return null;
    tokens.push({ kind: 'term', result: evaluateRule(rule, getFieldValue) });
    at = start + rule.fullCondition.length;
  }
  return pushJoiners(rulesExpression.slice(at), tokens) ? tokens : null;
}

/** expression := and (OR and)* ; and := factor (AND factor)* ; factor := term | ( expression ). */
function combine(tokens: Token[]): boolean | null {
  let at = 0;
  const expression = (): boolean | null => {
    let result = andGroup();
    while (result !== null && tokens[at]?.kind === 'or') {
      at++;
      const next = andGroup();
      result = next === null ? null : result || next;
    }
    return result;
  };
  const andGroup = (): boolean | null => {
    let result = factor();
    while (result !== null && tokens[at]?.kind === 'and') {
      at++;
      const next = factor();
      result = next === null ? null : result && next;
    }
    return result;
  };
  const factor = (): boolean | null => {
    const token = tokens[at++];
    if (token?.kind === 'term') return token.result;
    if (token?.kind !== 'open') return null;
    const inner = expression();
    return tokens[at++]?.kind === 'close' ? inner : null;
  };

  const result = expression();
  return at === tokens.length ? result : null;
}

/**
 * Binds a field-value resolver and returns a checkExpression function.
 * An empty or unreadable expression returns true (the item is shown).
 */
export function createRuleEvaluator(
  getFieldValue: (fieldId: string) => unknown,
): (rulesExpression: string) => boolean {
  return function checkExpression(rulesExpression: string): boolean {
    if (!rulesExpression) return true;
    const tokens = tokenise(rulesExpression, getFieldValue);
    const result = tokens && tokens.length ? combine(tokens) : null;
    return result ?? true;
  };
}

/**
 * For the legacy work order checklist, whose expressions are made of rule numbers instead of
 * conditions, e.g. "1 AND (2 OR 3)". `resolveTerm` turns each word into true/false (or undefined
 * if it is not a known term). Returns null when the expression cannot be read.
 */
export function evaluateTermExpression(
  expression: string,
  resolveTerm: (term: string) => boolean | undefined,
): boolean | null {
  const tokens: Token[] = [];
  for (const word of expression.match(/\(|\)|[^\s()]+/g) ?? []) {
    const upper = word.toUpperCase();
    if (word === '(') tokens.push({ kind: 'open' });
    else if (word === ')') tokens.push({ kind: 'close' });
    else if (upper === 'AND' || word === '&&') tokens.push({ kind: 'and' });
    else if (upper === 'OR' || word === '||') tokens.push({ kind: 'or' });
    else {
      const result = resolveTerm(word);
      if (result === undefined) return null;
      tokens.push({ kind: 'term', result });
    }
  }
  return tokens.length ? combine(tokens) : null;
}
